// Piezas del alcance que comparten el configurador de promos y el de códigos: columnas de SKU y de
// tienda con su buscador.
import { useState, type ReactNode } from 'react';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import type { Store } from '@/api/services/retail-org/retail-org.client';
import {
    ScopeChip,
    ScopeChipGroup,
    ScopeChipSearch,
    type ScopeDim,
} from '@/shared/patterns/ScopeChips';
import type { SourceState } from '../api/usePromotionFormSources';

/** Tope de chips de SKU que se pintan a la vez: el catálogo puede traer cientos. */
const MAX_SKU_CHIPS = 60;

export function toggleIn<T extends string>(list: readonly T[], value: T): T[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function productText(product: CatalogProduct): string {
    return [product.sku, product.brand, product.model].filter(Boolean).join(' ').toLowerCase();
}

/**
 * Chip «Todos/Todas» del mock al inicio de una columna: prendido cuando no hay nada elegido, y al
 * tocarlo limpia la dimensión.
 */
export function AllScopeChip({
    dim,
    label,
    selected,
    onClear,
}: {
    dim: ScopeDim;
    label: 'Todos' | 'Todas';
    selected: boolean;
    onClear: () => void;
}) {
    return (
        <div className="contents [&>button]:italic">
            <ScopeChip dim={dim} label={label} selected={selected} onToggle={onClear} />
        </div>
    );
}

export function SourceNotice({ state, what }: { state: SourceState; what: string }) {
    if (state === 'ready') return null;
    return (
        <p className="mt-1.5 text-[10px] text-muted-foreground">
            {state === 'loading' ? `Cargando ${what}…` : `No pudimos leer ${what}.`}
        </p>
    );
}

/** Columna «Producto (SKU)»: lo elegido primero, luego lo que coincide, en orden alfabético. */
export function SkuScopeGroup({
    title = 'Producto (SKU)',
    help,
    searchLabel,
    products,
    productsState,
    selected,
    onChange,
    withAllChip = false,
    readOnly = false,
}: {
    title?: string;
    /** Qué acota la columna; va en su «?». */
    help?: string;
    /** Nombre del buscador para lector de pantalla. */
    searchLabel: string;
    products: CatalogProduct[];
    productsState: SourceState;
    selected: string[];
    onChange: (skus: string[]) => void;
    /** Pinta el chip «Todos» al inicio de la columna. */
    withAllChip?: boolean;
    /** Solo lo elegido (o «Todos»), sin buscador. */
    readOnly?: boolean;
}) {
    const [search, setSearch] = useState('');
    const needle = search.trim().toLowerCase();
    const bySku = new Map(products.map((product) => [product.sku, product]));
    const others = products
        .filter((product) => !selected.includes(product.sku))
        .filter((product) => needle === '' || productText(product).includes(needle))
        .sort((a, b) => a.sku.localeCompare(b.sku, 'es-MX'));
    const shown = readOnly ? [] : others.slice(0, MAX_SKU_CHIPS);
    const detailOf = (sku: string) => {
        const product = bySku.get(sku);
        return product ? [product.brand, product.model].filter(Boolean).join(' ') : undefined;
    };
    const toggle = (sku: string) => {
        onChange(toggleIn(selected, sku));
    };
    return (
        <ScopeChipGroup
            title={title}
            help={help}
            search={
                readOnly ? undefined : (
                    <ScopeChipSearch
                        value={search}
                        onChange={setSearch}
                        placeholder="Buscar SKU…"
                        label={searchLabel}
                    />
                )
            }
            footer={<SourceNotice state={productsState} what="los productos" />}
        >
            {withAllChip ? (
                <AllScopeChip
                    dim="product"
                    label="Todos"
                    selected={selected.length === 0}
                    onClear={() => {
                        onChange([]);
                    }}
                />
            ) : null}
            {[...selected].sort().map((sku) => (
                <ScopeChip
                    key={sku}
                    dim="product"
                    label={sku}
                    detail={detailOf(sku)}
                    selected
                    onToggle={() => {
                        toggle(sku);
                    }}
                />
            ))}
            {shown.map((product) => (
                <ScopeChip
                    key={product.sku}
                    dim="product"
                    label={product.sku}
                    detail={detailOf(product.sku)}
                    selected={false}
                    onToggle={() => {
                        toggle(product.sku);
                    }}
                />
            ))}
            {!readOnly && others.length > shown.length ? (
                <p className="px-1 text-[10px] text-muted-foreground">
                    Hay más productos; busca por SKU, marca o modelo.
                </p>
            ) : null}
        </ScopeChipGroup>
    );
}

/** Columna «Tienda»: chips en orden alfabético con buscador. */
export function StoreScopeGroup({
    title = 'Tienda',
    help,
    searchLabel,
    stores,
    storesState,
    selected,
    onChange,
    withAllChip = false,
    readOnly = false,
    after,
}: {
    title?: string;
    /** Qué acota la columna; va en su «?». */
    help?: string;
    searchLabel: string;
    stores: Store[];
    storesState: SourceState;
    selected: string[];
    onChange: (storeIds: string[]) => void;
    /** Pinta el chip «Todas» al inicio de la columna. */
    withAllChip?: boolean;
    /** Solo lo elegido (o «Todas»), sin buscador. */
    readOnly?: boolean;
    /** Chips que van después de las tiendas. */
    after?: ReactNode;
}) {
    const [search, setSearch] = useState('');
    const needle = search.trim().toLowerCase();
    const names = new Map(stores.map((store) => [store.storeId, store.name]));
    // Una tienda guardada que ya no llega del padrón se pinta con su id, o no habría cómo quitarla.
    const options = [
        ...stores.map((store) => ({ storeId: store.storeId, name: store.name })),
        ...selected.filter((id) => !names.has(id)).map((id) => ({ storeId: id, name: id })),
    ]
        .filter(
            (option) =>
                selected.includes(option.storeId) ||
                (!readOnly && needle === '') ||
                (!readOnly && `${option.name} ${option.storeId}`.toLowerCase().includes(needle)),
        )
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
    return (
        <ScopeChipGroup
            title={title}
            help={help}
            search={
                readOnly ? undefined : (
                    <ScopeChipSearch
                        value={search}
                        onChange={setSearch}
                        placeholder="Buscar tienda…"
                        label={searchLabel}
                    />
                )
            }
            footer={<SourceNotice state={storesState} what="las tiendas de la cadena" />}
        >
            {withAllChip ? (
                <AllScopeChip
                    dim="store"
                    label="Todas"
                    selected={selected.length === 0}
                    onClear={() => {
                        onChange([]);
                    }}
                />
            ) : null}
            {options.map((option) => (
                <ScopeChip
                    key={option.storeId}
                    dim="store"
                    label={option.name}
                    selected={selected.includes(option.storeId)}
                    onToggle={() => {
                        onChange(toggleIn(selected, option.storeId));
                    }}
                />
            ))}
            {after}
        </ScopeChipGroup>
    );
}
